import React, { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, ExternalLink, Settings as SettingsIcon, BarChart3, Calendar, Sparkles, KeyRound, Loader2, CreditCard } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Dashboard() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", currency: "EUR" });
  const [stats, setStats] = useState({ views: 0, items: 0, scans: 0 });

  const trialDays = user?.trial_days_left ?? 0;
  const isTrial = user?.plan === "trial";
  const trialActive = user?.trial_active;
  const trialExpired = isTrial && !trialActive;

  useEffect(() => {
    if (trialExpired) {
      // soft redirect to pricing
      const timer = setTimeout(() => nav("/pricing"), 100);
      return () => clearTimeout(timer);
    }
  }, [trialExpired, nav]);

  const load = async () => {
    const { data } = await api.get("/restaurants");
    setRestaurants(data);
    let v = 0, items = 0, sc = 0;
    for (const r of data) {
      try {
        const s = await api.get(`/restaurants/${r.id}/stats`);
        v += s.data.views;
        sc += s.data.scans;
      } catch {}
      try {
        const it = await api.get(`/restaurants/${r.id}/items`);
        items += it.data.length;
      } catch {}
    }
    setStats({ views: v, items, scans: sc });
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    api.get("/payments/subscription").then((r) => setSubscription(r.data)).catch(() => setSubscription(null));
  }, []);

  const cancelSubscription = async () => {
    const msg = lang === "de"
      ? "Möchtest du dein Abonnement wirklich kündigen? Es bleibt bis zum Ende des bezahlten Zeitraums aktiv."
      : lang === "tr"
      ? "Aboneliğini iptal etmek istediğine emin misin? Ödediğin dönem sonuna kadar aktif kalır."
      : "Are you sure you want to cancel your subscription? It will remain active until the end of the paid period.";
    if (!window.confirm(msg)) return;
    setCancelBusy(true);
    try {
      await api.post("/payments/subscription/cancel");
      toast.success(lang === "de" ? "Abonnement gekündigt" : lang === "tr" ? "Abonelik iptal edildi" : "Subscription cancelled");
      const r = await api.get("/payments/subscription");
      setSubscription(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setCancelBusy(false);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/restaurants", form);
      setOpen(false);
      setForm({ name: "", description: "", currency: "EUR" });
      load();
      toast.success("Created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const pwTx = (k) => ({
    title: { de: "Passwort ändern", en: "Change password", tr: "Şifre değiştir" },
    current: { de: "Aktuelles Passwort", en: "Current password", tr: "Mevcut şifre" },
    newPw: { de: "Neues Passwort", en: "New password", tr: "Yeni şifre" },
    confirm: { de: "Neues Passwort bestätigen", en: "Confirm new password", tr: "Yeni şifre (tekrar)" },
    submit: { de: "Aktualisieren", en: "Update", tr: "Güncelle" },
    success: { de: "Passwort aktualisiert", en: "Password updated", tr: "Şifre güncellendi" },
    mismatch: { de: "Passwörter stimmen nicht überein", en: "Passwords do not match", tr: "Şifreler eşleşmiyor" },
    minLen: { de: "Mindestens 8 Zeichen", en: "At least 8 characters", tr: "En az 8 karakter" },
    btn: { de: "Passwort ändern", en: "Change password", tr: "Şifre değiştir" },
  })[k]?.[lang] || "";

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error(pwTx("mismatch"));
      return;
    }
    if (pwForm.new_password.length < 8) {
      toast.error(pwTx("minLen"));
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success(pwTx("success"));
      setPwOpen(false);
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Trial banner */}
        {isTrial && trialActive && (
          <div className="rounded-2xl border border-purple-500/30 bg-gradient-to-r from-purple-600/15 via-fuchsia-600/10 to-purple-600/15 p-4 lg:p-5 flex flex-wrap items-center gap-4" data-testid="trial-banner">
            <div className="w-11 h-11 rounded-xl bg-purple-600/20 grid place-items-center shrink-0">
              <Calendar className="h-5 w-5 text-purple-300" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-sm font-bold">
                {lang === "de" ? `Noch ${trialDays} Tage in deiner kostenlosen Testphase` : lang === "tr" ? `Ücretsiz deneme süreniz ${trialDays} gün kaldı` : `${trialDays} days left in your free trial`}
              </div>
              <div className="text-xs text-zinc-400 mt-0.5">
                {lang === "de" ? "Wähle einen Plan, um nach Ablauf weiterzumachen." : lang === "tr" ? "Süre dolduktan sonra devam etmek için bir plan seçin." : "Choose a plan to continue after the trial ends."}
              </div>
            </div>
            <Link to="/pricing">
              <Button className="rounded-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-600/30" data-testid="trial-upgrade-btn">
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                {lang === "de" ? "Plan wählen" : lang === "tr" ? "Plan seç" : "Choose plan"}
              </Button>
            </Link>
          </div>
        )}
        {trialExpired && (
          <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-5" data-testid="trial-expired-banner">
            <div className="text-sm font-bold text-rose-300">
              {lang === "de" ? "Deine Testphase ist abgelaufen" : lang === "tr" ? "Deneme süreniz sona erdi" : "Your trial has ended"}
            </div>
            <p className="text-xs text-zinc-400 mt-1">
              {lang === "de" ? "Wähle einen Plan, um deine Menüs weiterhin nutzen zu können." : lang === "tr" ? "Menülerinizi kullanmaya devam etmek için bir plan seçin." : "Choose a plan to continue using your menus."}
            </p>
            <Link to="/pricing">
              <Button className="mt-3 rounded-full bg-purple-600 hover:bg-purple-700 text-white">
                {lang === "de" ? "Jetzt upgraden" : lang === "tr" ? "Şimdi yükselt" : "Upgrade now"}
              </Button>
            </Link>
          </div>
        )}

        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
              {t("dashboard.welcome")}
            </div>
            <h1 className="text-3xl font-bold tracking-tight">{user?.name}</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-purple-600 hover:bg-purple-700 text-white shadow-lg shadow-purple-500/25" data-testid="add-restaurant-btn">
                <Plus className="h-4 w-4 mr-2" />
                {t("dashboard.addRestaurant")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>{t("restaurant.newRestaurant")}</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-4" data-testid="new-restaurant-form">
                <div className="space-y-1.5">
                  <Label>{t("restaurant.restaurantName")}</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl" data-testid="restaurant-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("restaurant.description")}</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl" data-testid="restaurant-desc-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("restaurant.currency")}</Label>
                  <select className="w-full h-10 rounded-xl border bg-background px-3" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="restaurant-currency-input">
                    <option value="EUR">EUR €</option>
                    <option value="USD">USD $</option>
                    <option value="TRY">TRY ₺</option>
                    <option value="AED">AED د.إ</option>
                  </select>
                </div>
                <DialogFooter>
                  <Button type="submit" className="rounded-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="restaurant-submit-btn">{t("common.save")}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Account actions row */}
        <div className="flex flex-wrap items-center gap-2">
          {subscription?.active && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-xs font-medium" data-testid="subscription-status">
              <CreditCard className="h-3.5 w-3.5" />
              {lang === "de" ? "Abonnement aktiv" : lang === "tr" ? "Abonelik aktif" : "Subscription active"}
              {" · "}
              <span className="font-bold uppercase">{subscription.subscription?.plan_id} {subscription.subscription?.billing_period}</span>
            </div>
          )}
          {subscription?.active && (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-full text-rose-600 hover:text-rose-700 hover:bg-rose-500/10"
              onClick={cancelSubscription}
              disabled={cancelBusy}
              data-testid="cancel-subscription"
            >
              {cancelBusy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {lang === "de" ? "Abonnement kündigen" : lang === "tr" ? "Aboneliği iptal et" : "Cancel subscription"}
            </Button>
          )}
          <Dialog open={pwOpen} onOpenChange={setPwOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="rounded-full" data-testid="open-change-password">
                <KeyRound className="h-3.5 w-3.5 mr-1.5" />
                {pwTx("btn")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>{pwTx("title")}</DialogTitle></DialogHeader>
              <form onSubmit={changePassword} className="space-y-4" data-testid="change-password-form">
                <div className="space-y-1.5">
                  <Label htmlFor="cur-pw">{pwTx("current")}</Label>
                  <Input id="cur-pw" type="password" required value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} className="rounded-xl" data-testid="cur-pw-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="new-pw">{pwTx("newPw")}</Label>
                  <Input id="new-pw" type="password" required minLength={8} value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} className="rounded-xl" data-testid="new-pw-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirm-pw">{pwTx("confirm")}</Label>
                  <Input id="confirm-pw" type="password" required minLength={8} value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} className="rounded-xl" data-testid="confirm-pw-input" />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={pwBusy} className="rounded-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="submit-change-password">
                    {pwBusy && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                    {pwTx("submit")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { label: t("dashboard.totalViews"), value: stats.views, color: "from-purple-500 to-purple-500" },
            { label: t("dashboard.totalScans"), value: stats.scans, color: "from-fuchsia-500 to-pink-500" },
            { label: t("dashboard.totalItems"), value: stats.items, color: "from-emerald-500 to-teal-500" },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl border bg-card p-5">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{s.label}</div>
              <div className={`text-3xl font-bold mt-1 bg-gradient-to-br ${s.color} bg-clip-text text-transparent`}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {/* Restaurants */}
        <div>
          <h2 className="text-xl font-bold mb-3">{t("dashboard.yourRestaurants")}</h2>
          {restaurants.length === 0 ? (
            <div className="rounded-2xl border border-dashed p-12 text-center text-muted-foreground" data-testid="no-restaurants">
              {t("dashboard.noRestaurants")}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {restaurants.map((r) => (
                <Link key={r.id} to={`/dashboard/restaurants/${r.id}`} className="group rounded-2xl border bg-card overflow-hidden hover:-translate-y-0.5 hover:shadow-lg hover:border-purple-500/30 transition-all block" data-testid={`restaurant-card-${r.id}`}>
                  <div className="h-28 relative overflow-hidden bg-gradient-to-br from-purple-500 via-purple-500 to-fuchsia-500">
                    {r.cover_image && <img src={r.cover_image} alt="" className="w-full h-full object-cover" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                    <div className="absolute bottom-3 left-4 right-4 text-white">
                      <div className="text-xs opacity-80">{r.currency}</div>
                      <div className="text-lg font-bold truncate">{r.name}</div>
                    </div>
                    {user?.role === "admin" && r.owner_email && r.owner_id !== user?.id && (
                      <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-fuchsia-500/90 text-white text-[10px] font-semibold backdrop-blur" data-testid={`owner-badge-${r.id}`}>
                        @ {r.owner_email}
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">{r.description || "—"}</p>
                    <div className="mt-3 flex gap-2" onClick={(e) => e.stopPropagation()}>
                      <Button variant="outline" size="sm" className="w-full rounded-full flex-1" data-testid={`open-restaurant-${r.id}`}>
                        <SettingsIcon className="h-3.5 w-3.5 mr-1.5" />
                        {t("common.settings")}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="rounded-full"
                        data-testid={`view-public-${r.id}`}
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(`/m/${r.slug}`, "_blank"); }}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
